import Debug from 'debug'
import { useInfiniteQuery } from '@tanstack/react-query'

import type {
  GetUpdatesResponse,
  Update,
} from '@jotta/grpc-js-client/updateService'
import { listUpdates, markAsRead } from '@jotta/grpc-js-client/updateService'
import type { Notification } from '@jotta/types/Notifications'

export interface NotificationsApi {
  fetch: (next?: string) => Promise<GetUpdatesResponse.AsObject>
  markAsRead: (update: Update.AsObject) => Promise<unknown>
}

export const notificationsApi: NotificationsApi = {
  fetch: next =>
    listUpdates({
      cursor: next,
    }),
  markAsRead: update =>
    markAsRead({
      update,
    }),
}

const debug = Debug('jotta:ui:NotificationsApi')

export function useNotifications(api = notificationsApi) {
  const query = useInfiniteQuery({
    queryKey: ['notifications'],
    queryFn: ({ pageParam }) => api.fetch(pageParam as string | undefined),
    getNextPageParam: lastPage => lastPage.cursor || undefined,
    initialPageParam: '',
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  })

  const pages = query.data?.pages ?? []
  const firstPage = pages[0]

  // Always a number (default 0)
  const unreadCount: number = firstPage?.newUpdatesCount ?? 0
  const hasUnread = unreadCount > 0

  const notifications: Notification[] = pages.flatMap(p => p.updateList ?? [])

  return {
    ...query,
    notifications,
    unreadCount,
    hasUnread,
  }
}
